<script setup lang="ts">
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Toaster } from "vue-sonner";
import { ThemeSetting, useTheme } from "../../composables/Theme/Theme";
import { PROGRESS_TOASTER_ID } from "./toasterIds";
import ToastTypeIcon from "./ToastTypeIcon.vue";
import "vue-sonner/style.css";
import "./toaster.css";

const EDGE_OFFSET = 16;
const MOBILE_EDGE_OFFSET = 8;
// vue-sonner's default space between two toasts.
const TOAST_GAP = 14;
const PROGRESS_SELECTOR = "ol.vb-progress-toaster li[data-sonner-toast][data-visible='true']:not([data-removed='true'])";

const { t } = useI18n();
const { theme } = useTheme();

const root = ref<HTMLElement | null>(null);
// vue-sonner expands the stack on hover only, so keyboard focus inside the toasts expands it here.
const expandedByFocus = ref(false);
// Room the progress toasts take at the top, so the normal toasts start right below them.
const progressInset = ref(0);

// Unstyled: toaster.css draws the card (class `vb-toast`) for normal and progress toasts alike.
const toastOptions = computed(() => ({
  unstyled: true,
  class: "vb-toast",
  closeButtonAriaLabel: t("base.close"),
}));

const normalOffset = computed(() => ({
  top: EDGE_OFFSET + progressInset.value,
  right: EDGE_OFFSET,
  bottom: EDGE_OFFSET,
  left: EDGE_OFFSET,
}));

const normalMobileOffset = computed(() => ({
  top: MOBILE_EDGE_OFFSET + progressInset.value,
  right: MOBILE_EDGE_OFFSET,
  bottom: MOBILE_EDGE_OFFSET,
  left: MOBILE_EDGE_OFFSET,
}));

const mobileOffset = {
  top: MOBILE_EDGE_OFFSET,
  right: MOBILE_EDGE_OFFSET,
  bottom: MOBILE_EDGE_OFFSET,
  left: MOBILE_EDGE_OFFSET,
};

function onFocusOut(event: FocusEvent) {
  const container = event.currentTarget as Node;
  if (event.relatedTarget instanceof Node && container.contains(event.relatedTarget)) {
    return;
  }
  expandedByFocus.value = false;
}

const observedItems = new Set<Element>();
let resizeObserver: ResizeObserver | undefined;
let mutationObserver: MutationObserver | undefined;

function measureProgressStack() {
  if (!root.value) {
    return;
  }
  const items = Array.from(root.value.querySelectorAll<HTMLElement>(PROGRESS_SELECTOR));
  for (const observed of observedItems) {
    if (!items.includes(observed as HTMLElement)) {
      resizeObserver?.unobserve(observed);
      observedItems.delete(observed);
    }
  }
  for (const item of items) {
    if (!observedItems.has(item)) {
      observedItems.add(item);
      resizeObserver?.observe(item);
    }
  }
  const height = items.reduce((total, item) => total + item.offsetHeight, 0);
  progressInset.value = items.length > 0 ? height + items.length * TOAST_GAP : 0;
}

onMounted(() => {
  if (!root.value) {
    return;
  }
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(measureProgressStack);
  }
  if (typeof MutationObserver !== "undefined") {
    mutationObserver = new MutationObserver(measureProgressStack);
    mutationObserver.observe(root.value, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-visible", "data-removed", "data-mounted"],
    });
  }
  measureProgressStack();
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  mutationObserver?.disconnect();
  observedItems.clear();
});
</script>

<template>
  <div ref="root" style="display: contents" @focusin="expandedByFocus = true" @focusout="onFocusOut">
    <!-- Progress toasts: always above the normal toasts, all shown at full height. -->
    <Toaster
      :id="PROGRESS_TOASTER_ID"
      class="vb-progress-toaster"
      :theme="theme === ThemeSetting.DARK ? 'dark' : 'light'"
      position="top-right"
      :offset="EDGE_OFFSET"
      :mobile-offset="mobileOffset"
      :visible-toasts="3"
      :expand="true"
      :toast-options="toastOptions"
      :container-aria-label="t('base.toast_container_label')"
    />
    <Toaster
      class="vb-normal-toaster"
      :theme="theme === ThemeSetting.DARK ? 'dark' : 'light'"
      position="top-right"
      :offset="normalOffset"
      :mobile-offset="normalMobileOffset"
      :visible-toasts="3"
      :expand="expandedByFocus"
      close-button
      :toast-options="toastOptions"
      :container-aria-label="t('base.toast_container_label')"
    >
      <template #success-icon>
        <ToastTypeIcon type="success" />
      </template>
      <template #info-icon>
        <ToastTypeIcon type="info" />
      </template>
      <template #warning-icon>
        <ToastTypeIcon type="warning" />
      </template>
      <template #error-icon>
        <ToastTypeIcon type="error" />
      </template>
      <template #loading-icon>
        <ToastTypeIcon type="loading" />
      </template>
      <template #close-icon>
        <VIcon name="x" aria-hidden="true" />
      </template>
    </Toaster>
  </div>
</template>
